import { Section, EyebrowHeading } from "./Section";
import { ShieldCheck, Calendar, Clock, Sparkles, FileCheck, Layers, CalendarCheck, RefreshCw } from "lucide-react";
import { cn } from "@/lib/utils";
import { useLocale } from "@/i18n/LocaleProvider";

type Variant = "pulse" | "prime";

interface Guarantee {
  icon: typeof ShieldCheck;
  title: string;
  body: string;
}

// --- Pulse (löpande abonnemang) -------------------------------------------
const pulseSv: Guarantee[] = [
  { icon: ShieldCheck, title: "Fast månadspris", body: "Inga offertspel, inga överraskningar. Ni vet exakt vad ni betalar — och vad ni får." },
  { icon: Calendar, title: "Säg upp månadsvis", body: "Inga årsbindningar. Pausa eller skala upp efter er kvartalsrytm." },
  { icon: Clock, title: "Första klippet inom 10–14 dagar", body: "Vi etablerar tempo direkt. Ni ser leverans, inte planering." },
  { icon: Sparkles, title: "Senior finish, alltid", body: "Samma producent, regissör, klippare och colorist genom hela samarbetet." },
];

const pulseEn: Guarantee[] = [
  { icon: ShieldCheck, title: "Fixed monthly price", body: "No quote games, no surprises. You know exactly what you pay — and what you get." },
  { icon: Calendar, title: "Cancel monthly", body: "No annual lock-in. Pause or scale up to fit your quarterly rhythm." },
  { icon: Clock, title: "First cut within 14 days", body: "We set the pace right away. You see delivery, not planning." },
  { icon: Sparkles, title: "Senior finish, always", body: "The same producer, director, editor and colorist across the whole partnership." },
];

// --- Prime (projektproduktion) --------------------------------------------
const primeSv: Guarantee[] = [
  { icon: FileCheck, title: "Fast offert innan start", body: "Manus, plan och estimat är spikade innan vi rullar. Inga öppna timpriser, inga efterhandsöverraskningar." },
  { icon: Layers, title: "Full förproduktion", body: "Scouting, casting, riggplan och provspelningar löst i förväg — så inspelningsdagen alltid sitter." },
  { icon: CalendarCheck, title: "97 % på premiärdatum", body: "Vi planerar baklänges från er deadline. Nästan alla Prime-produktioner levereras på utsatt dag." },
  { icon: RefreshCw, title: "Revideringar tills det sitter", body: "Klipp, grading och ljud justeras tills filmen håller den nivå vi lovat. Ingen halvfärdig leverans." },
];

const primeEn: Guarantee[] = [
  { icon: FileCheck, title: "Fixed quote before start", body: "Script, plan and estimate are locked before we roll. No open hourly rates, no after-the-fact surprises." },
  { icon: Layers, title: "Full pre-production", body: "Scouting, casting, rig plan and test shoots solved in advance — so the shoot day always lands." },
  { icon: CalendarCheck, title: "97 % on premiere date", body: "We plan backwards from your deadline. Almost every Prime production delivers on the agreed day." },
  { icon: RefreshCw, title: "Revisions until it's right", body: "Cut, grading and sound are tuned until the film hits the level we promised. No half-finished delivery." },
];

const COPY = {
  pulse: {
    sv: pulseSv,
    en: pulseEn,
    eyebrowSv: "Ingen risk i att börja",
    eyebrowEn: "No risk in starting",
    ledeSv: "De flesta som byter från traditionell byrå är brända av samma fyra saker. Vi designade Fenny för att lösa dem från dag ett.",
    ledeEn: "Most teams switching from a traditional agency are burned by the same four things. We designed Fenny to solve them from day one.",
  },
  prime: {
    sv: primeSv,
    en: primeEn,
    eyebrowSv: "Ingen risk i en stor produktion",
    eyebrowEn: "No risk in a big production",
    ledeSv: "En större produktion känns riskfylld när priset är öppet och deadline osäker. Prime tar bort de fyra sakerna som brukar gå snett.",
    ledeEn: "A bigger production feels risky when the price is open and the deadline uncertain. Prime removes the four things that usually go wrong.",
  },
} as const;

export function RiskRemoval({ variant = "pulse" }: { variant?: Variant }) {
  const en = useLocale() === "en";
  const c = COPY[variant];
  const guarantees = en ? c.en : c.sv;
  const accent = variant === "prime" ? "butter" : "mint";

  return (
    <Section>
      <EyebrowHeading
        eyebrow={en ? c.eyebrowEn : c.eyebrowSv}
        title={
          en ? (
            <>We remove the four <span className={accent === "butter" ? "text-butter" : "text-mint"}>big worries</span>.</>
          ) : (
            <>Vi tar bort de fyra <span className={accent === "butter" ? "text-butter" : "text-mint"}>stora oron</span>.</>
          )
        }
        lede={en ? c.ledeEn : c.ledeSv}
      />

      <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {guarantees.map((g) => (
          <article key={g.title} className="flex flex-col rounded-3xl border border-white/10 bg-surface p-7">
            <div
              className={cn(
                "inline-flex h-11 w-11 items-center justify-center rounded-full ring-1",
                accent === "butter"
                  ? "bg-butter/15 text-butter ring-butter/30"
                  : "bg-mint/15 text-mint ring-mint/30",
              )}
            >
              <g.icon className="h-5 w-5" strokeWidth={2} />
            </div>
            <h3 className="mt-5 text-lg font-semibold leading-tight">{g.title}</h3>
            <p className="mt-2.5 text-sm leading-relaxed text-muted-foreground">{g.body}</p>
          </article>
        ))}
      </div>
    </Section>
  );
}
